import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { storeApi } from "@/lib/storefront";

const CART_KEY = "store_cart_v1";
const PRUNE_THROTTLE_MS = 30_000;
const CartContext = createContext(null);

function readCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn("Cart persist failed:", e);
  }
}

export function CartProvider({ children }) {
  // Hydrate synchronously from localStorage to avoid flash + overwrite race
  const [items, setItems] = useState(() => {
    try { return typeof window !== "undefined" ? readCart() : []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const hydrated = useRef(true);
  const lastPruneRef = useRef(0);
  const location = useLocation();

  // Persist on every cart change (skipping the first tick is not needed now
  // because useState initializer already read localStorage)
  useEffect(() => {
    if (!hydrated.current) return;
    writeCart(items);
  }, [items]);

  // Drop cart lines whose products were deleted/hidden in admin. We never want
  // a customer to reach checkout with a ghost item.
  const prune = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastPruneRef.current < PRUNE_THROTTLE_MS) return;
    const current = readCart();
    if (current.length === 0) return;
    try {
      const ids = current.map((p) => p.product_id).filter(Boolean);
      const { data } = await storeApi.post("/products/check-alive", { ids });
      const alive = new Set(data?.alive || []);
      const pruned = current.filter((p) => alive.has(p.product_id));
      lastPruneRef.current = now;
      if (pruned.length !== current.length) {
        setItems(pruned);
      }
    } catch {
      // Network hiccup — keep cart, retry later
    }
  }, []);

  useEffect(() => { prune(true); }, [prune]);
  useEffect(() => { prune(); }, [location.pathname, prune]);
  useEffect(() => {
    const onFocus = () => prune();
    const onVisible = () => { if (document.visibilityState === "visible") prune(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [prune]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          title: product.title,
          image: product.primary_image || "",
          price: Number(product.price || 0),
          currency: product.currency || "USD",
          asin: product.asin || "",
          sku: product.sku || "",
          seo_slug: product.seo_slug || product.id,
          quantity,
        },
      ];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((productId, quantity) => {
    setItems((prev) => prev
      .map((x) => (x.product_id === productId ? { ...x, quantity: Math.max(1, Number(quantity) || 1) } : x))
      .filter((x) => x.quantity > 0));
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((x) => x.product_id !== productId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 1), 0);
    const count = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
    return { subtotal: Number(subtotal.toFixed(2)), count };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, ...totals, open, setOpen, addItem, updateQty, removeItem, clear }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
